import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShoppingBasket } from "lucide-react";
import { getCart } from "@/lib/cart";
import CartDrawer from "@/components/site/CartDrawer";

const LOGO_ICON = "/logo-emblem.png";

const LINKS = [
  { href: "#stock", label: "Stock", testId: "nav-link-stock" },
  { href: "#valuation", label: "Valuations", testId: "nav-link-valuation" },
  { href: "#trade", label: "Sell & Trade", testId: "nav-link-trade" },
  { href: "#socials", label: "Socials", testId: "nav-link-socials" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [cart, setCartState] = useState(() => getCart());
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    const sync = () => setCartState(getCart());
    window.addEventListener("sx-cart", sync);
    return () => window.removeEventListener("sx-cart", sync);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#F3EDE3]/85 backdrop-blur-md border-b border-[#DCD3C3]" : "bg-transparent"}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_ICON} alt="Stud Exchange logo" className="w-10 h-10 rounded-full object-cover border border-[#DCD3C3]" />
          <div className="leading-none">
            <p className="font-serif font-semibold tracking-wide text-sm">STUD EXCHANGE</p>
            <p className="font-mono text-[9px] tracking-[0.3em] text-[#B38728] mt-1">BUY • SELL • TRADE • COLLECT</p>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.testId} className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#5C564C] hover:text-[#0A0A0A] transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCartOpen(true)}
            data-testid="basket-button"
            aria-label="Open basket"
            className="relative p-2.5 border border-[#DCD3C3] text-[#0A0A0A] hover:border-[#B38728] hover:text-[#B38728] transition-colors"
          >
            <ShoppingBasket className="w-5 h-5" />
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#B38728] text-[#0A0A0A] font-mono text-[9px] w-5 h-5 rounded-full flex items-center justify-center" data-testid="basket-count">
                {cart.length}
              </span>
            )}
          </button>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-[#F3EDE3] border-b border-[#DCD3C3] px-6 pb-6 space-y-4">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} data-testid={`${l.testId}-mobile`} className="block font-mono text-xs uppercase tracking-[0.25em] text-[#5C564C]">
              {l.label}
            </a>
          ))}
          <Link to="/admin" className="block font-mono text-xs uppercase tracking-[0.25em] text-[#B38728]" data-testid="nav-admin-link-mobile">
            Admin
          </Link>
        </div>
      )}
      </header>
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} />
    </>
  );
}
